import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AuthPage from "@/pages/AuthPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import VerifyMagicLink from "@/pages/VerifyMagicLink";
import GoogleCallback from "@/pages/GoogleCallback";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChatPage from "@/pages/ChatPage";
import AdminDashboard from "@/pages/AdminDashboard";
import CityDashboard from "@/pages/CityDashboard";
import UploadPage from "@/pages/UploadPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ConversationsView from "@/pages/ConversationsView";
import CompDatabasePage from "@/pages/CompDatabasePage";
import StudioPage from "@/pages/StudioPage";
import RoadmapPage from "@/pages/RoadmapPage";
import ApplicantHome from "@/pages/ApplicantHome";
import MonthlyReportPage from "@/pages/MonthlyReportPage";

// Stable role arrays — defined outside component to prevent re-renders
const ADMIN_ROLES = ["admin", "city_official"];
const ALL_ROLES = ["admin", "city_official", "client"];
const CLIENT_ROLES = ["client"];
import AdminTestChat from "@/pages/AdminTestChat";
import FlaggedMessagesPage from "@/pages/FlaggedMessagesPage";
import CityLoginPage from "@/pages/CityLoginPage";
import InvitesPage from "@/pages/InvitesPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import LongBeachPage from "@/pages/LongBeachPage";
import { Toaster } from "@/components/ui/sonner";

const dashboardPathForRole = (role) => {
  if (role === "admin") return "/admin";
  if (role === "city_official") return "/city";
  if (role === "client") return "/home";
  return "/"; // unknown/missing role → back to login
};

function ProtectedRoute({ children, requiredRole }) {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  
  if (!token) {
    return <Navigate to="/" replace />;
  }
  
  if (requiredRole && role !== requiredRole && !requiredRole.includes(role)) {
    // Logged in but this route isn't for their role — send them to their own
    // dashboard instead of the login page (avoids a confusing "didn't load" bounce).
    return <Navigate to={dashboardPathForRole(role)} replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AuthPage />} />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          <Route path="/city-login" element={<CityLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/google-callback" element={<GoogleCallback />} />
          <Route path="/verify" element={<VerifyMagicLink />} />
          <Route path="/home" element={
            <ProtectedRoute requiredRole={CLIENT_ROLES}>
              <ApplicantHome />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute requiredRole="client">
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="/comps" element={
            <ProtectedRoute requiredRole={ALL_ROLES}>
              <CompDatabasePage />
            </ProtectedRoute>
          } />
          <Route path="/studio" element={
            <ProtectedRoute requiredRole={ALL_ROLES}>
              <StudioPage />
            </ProtectedRoute>
          } />
          <Route path="/roadmap" element={
            <ProtectedRoute requiredRole={CLIENT_ROLES}>
              <RoadmapPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/upload" element={
            <ProtectedRoute requiredRole="admin">
              <UploadPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/test-chat" element={
            <ProtectedRoute requiredRole="admin">
              <AdminTestChat />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute requiredRole={ADMIN_ROLES}>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/report" element={
            <ProtectedRoute requiredRole={ADMIN_ROLES}>
              <MonthlyReportPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/conversations" element={
            <ProtectedRoute requiredRole={ADMIN_ROLES}>
              <ConversationsView />
            </ProtectedRoute>
          } />
          <Route path="/admin/flagged-messages" element={
            <ProtectedRoute requiredRole="admin">
              <FlaggedMessagesPage />
            </ProtectedRoute>
          } />

          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/longbeach" element={<LongBeachPage />} />
          <Route path="/longbeach/" element={<LongBeachPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          <Route path="/admin/invites" element={
            <ProtectedRoute requiredRole={ADMIN_ROLES}>
              <InvitesPage />
            </ProtectedRoute>
          } />

          <Route path="/city" element={
            <ProtectedRoute requiredRole="city_official">
              <CityDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
